// arbeitgeber.ts — WER ist der Arbeitgeber dieser Person?
//
// Der Arbeitgeber ist keine Spalte am Kontakt, sondern eine der vorhandenen
// Person↔Firma-Beziehungen (PO 19.08.2026). Anschrift und Branche werden aus
// der Firmenakte ANGEZEIGT, nicht kopiert — sonst müsste jeder Umzug des
// Arbeitgebers doppelt gepflegt werden.
//
// Diese Datei enthält nur die Entscheidung, rein und ohne React/DOM:
// Beziehungen + Freitext rein, ein Befund raus. Sie sitzt auf der bereits
// vorhandenen Ableitung `derivePersonEmployment` auf, damit es nicht zwei
// Vorstellungen davon gibt, welche Firmen zu einer Person gehören.

import {
  derivePersonEmployment,
  type EmpRelation,
  type EmploymentCompany,
} from "./derivePersonEmployment";

export type ArbeitgeberBefund =
  /** Genau eine Firma steht fest — sie wird mit Anschrift und Branche gezeigt. */
  | { art: "verknuepft"; firma: EmploymentCompany }
  /**
   * Mehrere Firmen kommen in Frage und keine ist markiert. Die Oberfläche muss
   * dann fragen, welche es ist — ein stilles „die erste" wäre geraten.
   */
  | { art: "mehrdeutig"; kandidaten: EmploymentCompany[] }
  /** Keine Verknüpfung, aber ein Freitextname aus dem Altbestand. */
  | { art: "freitext"; name: string }
  /** Weder das eine noch das andere. */
  | { art: "keiner" };

/**
 * Ermittelt den Arbeitgeber aus den Firmen-Verbindungen der Person.
 *
 * Reihenfolge der Regeln — und warum sie so ist:
 *  1. Eine ausdrücklich markierte Beziehung (`istHauptarbeitgeber`) gewinnt
 *     immer. Sie ist die Antwort eines Menschen auf die Frage, welche der
 *     Firmen der Arbeitgeber ist.
 *  2. Sonst zählen die Firmen mit dem bAV/bKV-Haken „Mensch im Unternehmen".
 *     Ist es genau eine, IST sie der Arbeitgeber — die Daten sagen es bereits,
 *     eine zusätzliche Markierung wäre reine Doppelpflege. Sind es mehrere,
 *     bleibt es mehrdeutig, bis jemand entscheidet.
 *  3. Sonst der Freitext `jobEmployer`. Er ist die Rückfallebene für alle, die
 *     nicht als Kontakt angelegt sind (im Bestand rund 1.810 Namen) und darf
 *     durch die Verknüpfung nie verlorengehen.
 *
 * Bewusst NICHT als Kandidat: eine Firma, mit der die Person nur als
 * Gesellschafterin oder als „Mensch fürs Unternehmen" (extern, etwa der
 * Steuerberater) verbunden ist. Beteiligung und Auftrag sind kein
 * Anstellungsverhältnis — genau dafür gibt es den Haken.
 */
export function arbeitgeberErmitteln(
  relationsSrc?: EmpRelation[] | null,
  relationsDest?: EmpRelation[] | null,
  freitext?: string | null,
): ArbeitgeberBefund {
  const firmen = derivePersonEmployment(relationsSrc, relationsDest, {
    inklusiveMenschImUnternehmen: true,
  });

  const markiert = firmen.filter((f) => f.istHauptarbeitgeber);
  if (markiert.length > 0) return { art: "verknuepft", firma: markiert[0] };

  const kandidaten = firmen.filter((f) => f.istMenschImUnternehmen);
  if (kandidaten.length === 1) return { art: "verknuepft", firma: kandidaten[0] };
  if (kandidaten.length > 1) return { art: "mehrdeutig", kandidaten };

  const name = (freitext ?? "").trim();
  if (name) return { art: "freitext", name };
  return { art: "keiner" };
}

/**
 * Alle Firmen, unter denen ein Arbeitgeber ausgewählt werden kann — Grundlage
 * der Rückfrage bei Mehrdeutigkeit. Firmen ohne Id fallen weg: ohne sie ließe
 * sich die Beziehung nicht markieren.
 */
export function arbeitgeberKandidaten(
  relationsSrc?: EmpRelation[] | null,
  relationsDest?: EmpRelation[] | null,
): EmploymentCompany[] {
  return derivePersonEmployment(relationsSrc, relationsDest, {
    inklusiveMenschImUnternehmen: true,
  }).filter((f) => f.companyId != null);
}
